<template>
  <div class="feed-page for-page">
    <!-- BAND 1: Beige — logo only -->
    <div class="band band-hero">
      <div class="band-content">
        <div class="page-header">
          <div class="brand-header">
            <router-link to="/">
              <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
            </router-link>
          </div>
          <router-link to="/" class="mobile-logo-link">
            <img src="/logo-red.png" alt="PassingOn" class="mobile-logo" />
          </router-link>
          <ProfileMenu v-if="isAuthenticated" />
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6"></v-progress-linear>
        <v-alert v-if="error" type="error" variant="tonal" class="mb-6">{{ error }}</v-alert>
      </div>
    </div>

    <template v-if="!loading && memorial">
      <!-- BAND 2: Dark — photo + details -->
      <div class="band band-details">
        <div class="band-content">
          <div class="hero-layout">
            <!-- Profile Photo -->
            <div class="hero-photo-container">
              <img
                v-if="memorial.profilePhoto"
                :src="getMediaUrl(memorial.profilePhoto)"
                :alt="memorial.nameOfPerson"
                class="hero-photo"
              />
              <div v-else class="hero-photo-placeholder">
                <v-icon size="120" color="grey-lighten-1">mdi-account</v-icon>
              </div>
            </div>

            <div class="hero-info">
              <!-- Date and Additional Details -->
              <div v-if="memorial.birthDate || memorial.deathDate || hasEvent" class="details-inner">

                <h1 v-if="memorial.birthDate && memorial.deathDate" class="life-heading">
                  {{ memorial.nameOfPerson || 'Memory' }}
                  <span>{{ formatLifeDate(memorial.birthDate) }} - {{ formatLifeDate(memorial.deathDate) }}</span>
                </h1>

                <div v-if="memorial.birthDate && !memorial.deathDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-cake-variant-outline</v-icon>
                  <span>Born {{ formatLifeDate(memorial.birthDate) }}</span>
                </div>

                <div v-if="memorial.deathDate && !memorial.birthDate" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-flower-outline</v-icon>
                  <span>Died {{ formatLifeDate(memorial.deathDate) }}</span>
                </div>

                <div v-if="hasEvent && memorial.eventName" class="hero-detail-item">
                  <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-calendar-star</v-icon>
                  <span>{{ memorial.eventName }}</span>
                </div>

                <!-- Date + address on the left, large map icon on the right spanning both rows -->
                <div v-if="hasEvent && (memorial.memorialDate || memorial.address)" class="details-top">
                  <div class="details-date-address">
                    <div v-if="memorial.memorialDate" class="hero-detail-item">
                      <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-calendar</v-icon>
                      <span>Event: {{ formatMemorialDate(memorial.memorialDate) }}<template v-if="memorial.memorialHour != null">&nbsp;&nbsp;{{ String(memorial.memorialHour).padStart(2, '0') }}:{{ String(memorial.memorialMinute ?? 0).padStart(2, '0') }}</template></span>
                    </div>
                    <button
                      v-if="memorial.address"
                      class="hero-detail-item address-toggle"
                      type="button"
                      @click="toggleMap"
                    >
                      <v-icon size="20" color="grey-lighten-2" class="mr-2">mdi-map-marker</v-icon>
                      <span>{{ memorial.address }}</span>
                    </button>
                  </div>
                  <img
                    v-if="memorial.address"
                    src="/images/map-icon.png"
                    alt=""
                    class="map-big-icon"
                    role="button"
                    tabindex="0"
                    @click="toggleMap"
                    @keydown.enter.prevent="toggleMap"
                    @keydown.space.prevent="toggleMap"
                  />
                </div>

                <!-- Details spans the full width (date + icon columns) -->
                <div v-if="hasEvent && memorial.details" class="hero-detail-item hero-details-text">
                  <span :class="{ 'details-collapsed': !detailsExpanded }">{{ memorial.details }}</span>
                  <button
                    v-if="detailsHasMore"
                    class="details-toggle"
                    type="button"
                    @click="toggleDetails"
                  >
                    {{ detailsExpanded ? 'less' : 'more' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Google Maps embed -->
          <div v-if="hasEvent && memorial.address && memorial.address.trim() && mapExpanded" class="hero-map mt-4">
            <iframe
              :src="mapSrc"
              width="100%"
              height="250"
              style="border:0; border-radius: 8px;"
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>

      <!-- Red stripe separator -->
      <div class="band-red-stripe"></div>

      <!-- BAND 3: White — action panel + host content -->
      <div class="band band-host">
        <div class="band-content">

        <!-- Contribution Panel -->
        <v-card class="main-card return-visitor-card mb-5" elevation="0">
          <v-card-text>
            <div class="panel-actions">
              <v-btn
                v-if="!isAuthenticated"
                color="primary"
                class="panel-btn"
                append-icon="mdi-login"
                @click="goToLogin"
              >
                Login
              </v-btn>
              <v-btn
                v-if="!userContribution && !showContributionSection"
                color="primary"
                variant="outlined"
                class="panel-btn"
                @click="toggleGuestContribution"
              >
                Contribute
              </v-btn>
              <v-btn
                v-if="hasEvent && !showRsvpSection"
                color="primary"
                variant="outlined"
                class="panel-btn"
                @click="toggleRsvpSection"
              >
                RSVP
                <span v-if="userRsvp" class="rsvp-button-status">
                  <v-icon color="success" size="16">mdi-check-circle</v-icon>
                  <span>Sent</span>
                </span>
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                class="panel-btn"
                @click="copyShareLink"
              >
                Share
              </v-btn>
              <v-btn
                v-if="hasDonationSetup"
                color="primary"
                variant="outlined"
                class="panel-btn"
                prepend-icon="mdi-hand-heart-outline"
                @click="goToDonate"
              >
                Donate
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <v-expand-transition>
          <v-card v-if="showContributionSection" id="qr-contribute-section" class="main-card mb-6" elevation="0">
            <v-card-title class="section-title">
              <v-icon left color="primary">mdi-heart-plus</v-icon>
              Contribute
            </v-card-title>
            <v-card-text>
              <p class="text-body-1 text--secondary mb-4 contribute-intro">
                Share your memories, photos, and stories to celebrate {{ memorial.nameOfPerson || 'their life' }}.
              </p>

              <!-- ── QR mode: simplified form, no password required ── -->
              <template v-if="qrMode">
                <v-alert v-if="qrSuccess" type="success" variant="tonal" class="mb-4">
                  Thank you — your contribution has been shared and is now visible on the memory.
                </v-alert>

                <template v-if="!qrSuccess">
                  <v-alert v-if="qrRequiresSignIn" type="info" variant="tonal" class="mb-4">
                    An account already exists for this email address. Please sign in to contribute.
                    <template v-slot:append>
                      <v-btn variant="text" size="small" @click="goToLoginForQr">Sign In</v-btn>
                    </template>
                  </v-alert>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contributorName"
                        label="Your name *"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contributorEmail"
                        label="Email address *"
                        type="email"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-textarea
                    v-model="contributorMessage"
                    label="Write a message (optional)"
                    variant="outlined"
                    rows="4"
                    auto-grow
                    class="mb-4"
                  ></v-textarea>

                  <div class="upload-area mb-4" @dragover.prevent @drop.prevent="handleDrop" @click="qrFileInput?.click()">
                    <v-icon size="48" color="primary" class="mb-2">mdi-cloud-upload-outline</v-icon>
                    <h4 class="text-h6 mb-1">Drop photos here or click to select</h4>
                    <p class="text-body-2 text--secondary mb-0">Supports JPG, PNG, GIF, HEIC files</p>
                    <input
                      ref="qrFileInput"
                      type="file"
                      accept="image/*,.heic,.HEIC"
                      multiple
                      class="upload-hidden-input"
                      @change="onManualFileSelect"
                    />
                  </div>

                  <div v-if="contributionPhotos.length" class="photo-preview-list mb-4">
                    <div
                      v-for="(photo, index) in contributionPhotos"
                      :key="photo.id || photo.preview"
                      class="photo-preview-row"
                    >
                      <div class="preview-thumb">
                        <img v-if="photo.preview" :src="photo.preview" alt="Uploaded photo" />
                      </div>
                      <div class="preview-details">
                        <v-textarea
                          v-model="photo.caption"
                          label="Add a story about this photo"
                          variant="outlined"
                          rows="2"
                          auto-grow
                        ></v-textarea>
                      </div>
                      <div class="preview-actions">
                        <v-btn variant="text" color="error" size="small" @click="removePhoto(index)">Remove</v-btn>
                      </div>
                    </div>
                  </div>

                  <div class="mb-5">
                    <h4 class="text-h6 mb-2">Add a voice memory</h4>
                    <VoiceContributionInput v-model="contributionAudios" />
                  </div>

                  <v-alert v-if="qrError" type="error" variant="tonal" class="mb-4">
                    {{ qrError }}
                  </v-alert>

                  <v-btn
                    color="primary"
                    size="large"
                    class="px-6"
                    :disabled="qrSubmitting"
                    :loading="qrSubmitting"
                    @click="handleQrContribution"
                  >
                    Share Your Memory
                  </v-btn>
                </template>
              </template>

              <!-- ── Standard form ── -->
              <template v-else>
                <p class="text-body-2 text--secondary mb-4">
                  If you have already contributed, please login.
                </p>

                <v-form ref="contributionForm">
                  <v-row v-if="!isAuthenticated">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contributorName"
                        label="Your name"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                      <p class="text-caption text--secondary mb-0">
                        Add a name so your public contribution shows your name instead of your email.
                      </p>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="contributorEmail"
                        label="Email address"
                        variant="outlined"
                        density="comfortable"
                        :error="contributionEmailError"
                        :error-messages="contributionEmailError ? ['Enter a valid email address'] : []"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-textarea
                    v-model="contributorMessage"
                    label="Write a message"
                    variant="outlined"
                    rows="4"
                    auto-grow
                    class="mb-2"
                  ></v-textarea>
                  <p class="text-caption text--secondary mb-4">
                    Share your thoughts or memories about {{ memorial.nameOfPerson || 'your loved one' }}. This will appear alongside the memory.
                  </p>

                  <div class="upload-area mb-4" @dragover.prevent @drop.prevent="handleDrop" @click="triggerFileSelect">
                    <v-icon size="64" color="primary" class="mb-2">
                      mdi-cloud-upload-outline
                    </v-icon>
                    <h4 class="text-h6 mb-2">Drop photos here or click to select</h4>
                    <p class="text-body-2 text--secondary mb-2">
                      Supports JPG, PNG, GIF, HEIC files
                    </p>
                    <input
                      ref="fileInput"
                      type="file"
                      accept="image/*,.heic,.HEIC"
                      multiple
                      class="upload-hidden-input"
                      @change="onManualFileSelect"
                    />
                  </div>

                  <div v-if="contributionPhotos.length" class="photo-preview-list mb-4">
                    <div
                      v-for="(photo, index) in contributionPhotos"
                      :key="photo.id || photo.preview"
                      class="photo-preview-row"
                    >
                      <div class="preview-thumb">
                        <img v-if="photo.preview" :src="photo.preview" alt="Uploaded photo" />
                      </div>
                      <div class="preview-details">
                        <v-textarea
                          v-model="photo.caption"
                          label="Add a story about this photo"
                          variant="outlined"
                          rows="2"
                          auto-grow
                        ></v-textarea>
                      </div>
                      <div class="preview-actions">
                        <v-btn variant="text" color="error" size="small" @click="removePhoto(index)">
                          Remove
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <div class="mb-5">
                    <h4 class="text-h6 mb-2">Add a voice memory</h4>
                    <VoiceContributionInput v-model="contributionAudios" />
                  </div>

                  <div class="contribution-actions">
                    <v-alert v-if="contributionError" type="error" variant="tonal" class="mb-4">
                      {{ contributionError }}
                    </v-alert>

                    <v-alert v-if="contributionSuccess" type="success" variant="tonal" class="mb-4">
                      {{ contributionSuccess }}
                    </v-alert>

                    <v-spacer></v-spacer>

                    <v-btn
                      color="primary"
                      size="large"
                      class="px-6"
                      :disabled="contributionSubmitting"
                      :loading="contributionSubmitting"
                      @click="isAuthenticated ? saveContribution() : openContributionDialog()"
                    >
                      {{ isAuthenticated ? 'Save Contribution' : 'Save & Contribute Now' }}
                    </v-btn>
                  </div>
                </v-form>
              </template>
            </v-card-text>
          </v-card>
        </v-expand-transition>

        <!-- Charity then sponsorship, asked once a contribution has been made -->
        <v-expand-transition>
          <PostContributionSupport
            v-if="showSupportAsk && hasDonationSetup"
            id="support-section"
            :slug="slug"
            :person-name="memorial.nameOfPerson || 'this memory'"
            :fund="memorial.memorialFunds?.[0] || null"
          />
        </v-expand-transition>

        <v-expand-transition>
          <v-card v-if="hasEvent && showRsvpSection" class="main-card mb-6" elevation="0">
            <v-card-title class="section-title rsvp-title" @click="toggleRsvpSection">
              <v-icon left color="primary">mdi-calendar-heart</v-icon>
              RSVP
            </v-card-title>
            <v-card-text>
              <v-alert v-if="isAuthenticated && userRsvp" type="success" variant="tonal" class="mb-4">
                RSVP sent {{ formatRsvpDate(userRsvp.dateCreated) }}
              </v-alert>
              <p class="text-body-2 text--secondary mb-4">
                We'd love to know if you can join us to remember {{ memorial.nameOfPerson || 'them' }}.
              </p>

              <v-row v-if="!isAuthenticated" class="mb-4">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="rsvpName"
                    label="Your name"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="rsvpEmail"
                    label="Email address"
                    type="email"
                    variant="outlined"
                    density="comfortable"
                    :error="rsvpEmailError"
                    :error-messages="rsvpEmailError ? 'Please enter a valid email address' : ''"
                    @blur="validateRsvpEmail"
                    @input="rsvpEmailError = false"
                  ></v-text-field>
                </v-col>
              </v-row>

              <div class="rsvp-actions mb-4">
                <v-btn-toggle
                  v-model="rsvpStatus"
                  mandatory
                  class="rsvp-toggle-group"
                  :disabled="rsvpSubmitting"
                >
                  <v-btn
                    v-for="option in rsvpOptions"
                    :key="option.value"
                    :value="option.value"
                    class="rsvp-option-btn"
                    :ripple="false"
                    color="primary"
                    :variant="rsvpStatus === option.value ? 'elevated' : 'tonal'"
                  >
                    <v-icon size="18" class="mr-2">
                      {{ rsvpStatus === option.value ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    {{ option.label }}
                  </v-btn>
                </v-btn-toggle>
              </div>

              <v-divider class="mb-4"></v-divider>

              <h4 class="text-h6 mb-2">Leave a private note</h4>
              <v-textarea
                v-model="rsvpMessage"
                label="Share a thought or message"
                variant="outlined"
                rows="4"
                auto-grow
                :disabled="rsvpSubmitting"
                class="mb-2"
              ></v-textarea>

              <p class="text-caption text--secondary mb-4">
                Your response and note are not seen by anyone other than the memory administrator
              </p>

              <v-alert v-if="rsvpSuccess" type="success" variant="tonal" class="mb-4">
                {{ rsvpResponse || 'Thank you for letting us know.' }}
              </v-alert>
              <v-alert v-if="rsvpError" type="error" variant="tonal" class="mb-4">
                {{ rsvpError }}
              </v-alert>

              <v-btn
                color="primary"
                :loading="rsvpSubmitting"
                :disabled="!rsvpStatus || rsvpSubmitting || (!isAuthenticated && !rsvpEmail)"
                @click="sendRsvp"
              >
                {{ userRsvp ? 'Send Updated Response' : 'Send Now' }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-expand-transition>


        <!-- User Contribution (Top) -->
          <div v-if="userContribution" class="contribution-section user-contribution mb-8" :class="userContributionClass">
            <div class="contributor-header mb-4">
              <v-avatar color="primary" size="40" class="mr-3">
                <span class="text-white font-weight-bold">{{ getInitials(userContribution.contributorName) }}</span>
              </v-avatar>
              <div>
                <div class="contributor-name">{{ userContribution.contributorName || 'You' }}</div>
                <div class="contributor-date text-caption text--secondary">
                  {{ formatDate(userContribution.dateCreated) }}
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-chip
                v-if="isUserContributionApproved"
                color="success"
                size="small"
                variant="tonal"
              >
                Approved
              </v-chip>
              <v-chip
                v-else
                color="warning"
                size="small"
                variant="tonal"
              >
                Awaiting approval
              </v-chip>
            </div>

            <div v-if="userContribution.message" class="contribution-message mb-4">
              <p class="text-body-1">{{ userContribution.message }}</p>
            </div>

            <div v-if="mediaPhotos(userContribution.media).length > 0" class="photos-list mb-4">
              <div
                v-for="photo in mediaPhotos(userContribution.media)"
                :key="photo.uuid"
                class="photo-row"
              >
                <div class="photo-left">
                  <img
                    :src="getMediaUrl(photo)"
                    :alt="photo.title || 'Contribution photo'"
                    class="photo-image"
                  />
                </div>
                <div v-if="photo.title" class="photo-right">
                  <p class="photo-caption">{{ photo.title }}</p>
                </div>
              </div>
            </div>

            <div v-if="mediaAudios(userContribution.media).length" class="audio-list mb-4">
              <AudioEntry v-for="audio in mediaAudios(userContribution.media)" :key="audio.uuid" :media="audio" :memorial-slug="slug" class="mb-4" />
            </div>

            <div class="d-flex justify-end">
              <v-btn variant="text" color="primary" @click="toggleEditContribution">
                {{ editContributionOpen ? 'Close' : 'Edit contribution' }}
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="editContributionOpen" class="edit-contribution mt-4">
                <v-textarea
                  v-model="contributorMessage"
                  label="Update your message"
                  variant="outlined"
                  rows="4"
                  auto-grow
                  class="mb-2"
                ></v-textarea>

                <div class="upload-area mb-4" @dragover.prevent @drop.prevent="handleDrop" @click="triggerFileSelect">
                  <v-icon size="64" color="primary" class="mb-2">
                    mdi-cloud-upload-outline
                  </v-icon>
                  <h4 class="text-h6 mb-2">Drop photos here or click to select</h4>
                  <p class="text-body-2 text--secondary mb-2">
                    Supports JPG, PNG, GIF, HEIC files
                  </p>
                  <input
                    ref="fileInput"
                    type="file"
                    accept="image/*,.heic,.HEIC"
                    multiple
                    class="upload-hidden-input"
                    @change="onManualFileSelect"
                  />
                </div>

                <div v-if="contributionPhotos.length" class="photo-preview-list mb-4">
                  <div
                    v-for="(photo, index) in contributionPhotos"
                    :key="photo.id || photo.preview"
                    class="photo-preview-row"
                  >
                    <div class="preview-thumb">
                      <img v-if="photo.preview" :src="photo.preview" alt="Uploaded photo" />
                    </div>
                    <div class="preview-details">
                      <v-textarea
                        v-model="photo.caption"
                        label="Add a story about this photo"
                        variant="outlined"
                        rows="2"
                        auto-grow
                      ></v-textarea>
                    </div>
                    <div class="preview-actions">
                      <v-btn variant="text" color="error" size="small" @click="removePhoto(index)">
                        Remove
                      </v-btn>
                    </div>
                  </div>
                </div>

                <div v-if="existingContributionAudios.length" class="mb-5">
                  <div v-for="(audio, index) in existingContributionAudios" :key="audio.uuid" class="mb-4">
                    <AudioEntry :media="audio" :memorial-slug="slug" />
                    <v-btn color="error" variant="text" size="small" prepend-icon="mdi-delete" @click="removeExistingAudio(index)">
                      Remove voice memory
                    </v-btn>
                  </div>
                </div>

                <div class="mb-5">
                  <h4 class="text-h6 mb-2">Add another voice memory</h4>
                  <VoiceContributionInput v-model="contributionAudios" />
                </div>

                <v-alert v-if="contributionError" type="error" variant="tonal" class="mb-4">
                  {{ contributionError }}
                </v-alert>
                <v-alert v-if="contributionSuccess" type="success" variant="tonal" class="mb-4">
                  {{ contributionSuccess }}
                </v-alert>

                <v-btn
                  color="primary"
                  size="large"
                  class="px-6"
                  :disabled="contributionSubmitting"
                  :loading="contributionSubmitting"
                  @click="saveContribution"
                >
                  Save updates
                </v-btn>
              </div>
            </v-expand-transition>
          </div>

          <!-- Owner's Photos - single contribution card -->
          <div v-if="ownerPhotos.length > 0" class="contribution-section mb-8">
            <div class="contributor-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>
              <span class="contributor-name">From {{ ownerFirstName }}</span>
            </div>
            <div class="photos-list">
              <div
                v-for="photo in ownerPhotos"
                :key="photo.uuid"
                class="photo-row"
              >
                <div class="photo-left">
                  <img
                    :src="getMediaUrl(photo)"
                    :alt="photo.title || 'Memory photo'"
                    class="photo-image"
                  />
                </div>
                <div v-if="photo.title" class="photo-right">
                  <p class="photo-caption">{{ photo.title }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="ownerAudios.length > 0" class="contribution-section mb-8">
            <div class="contributor-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-account-voice</v-icon>
              <span class="contributor-name">Voice memories from {{ ownerFirstName }}</span>
            </div>
            <AudioEntry v-for="audio in ownerAudios" :key="audio.uuid" :media="audio" :memorial-slug="slug" class="mb-4" />
          </div>

          <!-- Owner's Stories - contribution card with message styling per story -->
          <div v-if="ownerStories.length > 0" class="contribution-section mb-8">
            <div class="contributor-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>
              <span class="contributor-name">From {{ ownerFirstName }}</span>
            </div>
            <div
              v-for="story in ownerStories"
              :key="story.uuid"
              class="contribution-message mb-3"
            >
              <p>{{ story.text }}</p>
            </div>
          </div>

        </div>
      </div>

      <!-- BAND 4: White — contributions -->
      <div class="band band-contributions">
        <div class="band-content">

          <!-- Contributors' Content -->
          <div v-if="sortedContributions.length > 0" class="contributions-section">
            <div class="d-flex align-center justify-space-between mb-4">
              <h2 class="section-title mb-0">
                <v-icon class="mr-2" color="primary">mdi-heart-multiple</v-icon>
                Contributions from Friends & Family
              </h2>
            </div>

            <div
              v-for="contribution in sortedContributions"
              :key="contribution.id"
              class="contribution-section mb-8"
            >
              <div class="contributor-header mb-4">
                <v-avatar color="primary" size="40" class="mr-3">
                  <span class="text-white font-weight-bold">{{ getInitials(contribution.contributorName) }}</span>
                </v-avatar>
                <div>
                  <div class="contributor-name">{{ contribution.contributorName }}</div>
                  <div class="contributor-date text-caption text--secondary">
                    {{ formatDate(contribution.dateCreated) }}
                  </div>
                </div>
              </div>

              <!-- Contributor's Message -->
              <div v-if="contribution.message" class="contribution-message mb-4">
                <p class="text-body-1">{{ contribution.message }}</p>
              </div>

              <!-- Contributor's Photos - Photo left, caption right -->
              <div v-if="mediaPhotos(contribution.media).length > 0" class="photos-list mb-4">
                <div
                  v-for="photo in mediaPhotos(contribution.media)"
                  :key="photo.uuid"
                  class="photo-row"
                >
                  <div class="photo-left">
                    <img
                      :src="getMediaUrl(photo)"
                      :alt="photo.title || 'Contribution photo'"
                      class="photo-image"
                    />
                  </div>
                  <div v-if="photo.title" class="photo-right">
                    <p class="photo-caption">{{ photo.title }}</p>
                  </div>
                </div>
              </div>

              <div v-if="mediaAudios(contribution.media).length" class="audio-list mb-4">
                <AudioEntry v-for="audio in mediaAudios(contribution.media)" :key="audio.uuid" :media="audio" :memorial-slug="slug" class="mb-4" />
              </div>

              <v-divider v-if="sortedContributions.indexOf(contribution) < sortedContributions.length - 1" class="mt-6"></v-divider>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="ownerPhotos.length === 0 && ownerAudios.length === 0 && ownerStories.length === 0 && sortedContributions.length === 0" class="empty-state text-center py-12">
            <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-image-off</v-icon>
            <h3 class="text-h6 mb-2">No Content Yet</h3>
            <p class="text--secondary">Photos and stories will appear here once added.</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Contribution Signup & Verification Dialog -->
    <v-dialog v-model="showContributionDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h5">
          <template v-if="contributionDialogStep === 'credentials'">
            Create your account
          </template>
          <template v-else-if="contributionDialogStep === 'verify'">
            Verify your email
          </template>
          <template v-else>
            Contribution submitted
          </template>
        </v-card-title>
        <v-card-text>
          <template v-if="contributionDialogStep === 'credentials'">
            <p class="text-body-2 mb-4">
              We create a secure account so you can update your contribution later. Enter a password and we’ll send a verification code to {{ contributorEmail || 'your email' }}.
            </p>
            <v-text-field
              v-model="contributorPassword"
              label="Create password"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
            ></v-text-field>
            <v-text-field
              v-model="contributorPasswordConfirm"
              label="Confirm password"
              :type="showPasswordConfirm ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
            ></v-text-field>
            <v-alert v-if="contributionDialogError" type="error" variant="tonal" class="mt-2">
              {{ contributionDialogError }}
            </v-alert>
          </template>

          <template v-else-if="contributionDialogStep === 'verify'">
            <p class="text-body-2 mb-4">
              Enter the verification code we sent to {{ contributorEmail }}.
            </p>
            <v-text-field
              v-model="verificationCode"
              label="Verification code"
              variant="outlined"
              density="comfortable"
              maxlength="6"
            ></v-text-field>
            <v-btn
              variant="text"
              color="secondary"
              size="small"
              :loading="resendingCode"
              :disabled="resendCooldown > 0 || resendingCode"
              @click="resendContributionCode"
            >
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code' }}
            </v-btn>
            <v-alert v-if="contributionDialogError" type="error" variant="tonal" class="mt-2">
              {{ contributionDialogError }}
            </v-alert>
          </template>

          <template v-else>
            <v-alert type="success" variant="tonal" class="mb-2">
              {{ contributionCompletedMessage || 'Thank you for contributing. We have saved your memories.' }}
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="contributionDialogStep === 'credentials'">
            <v-btn variant="text" @click="closeContributionDialog" :disabled="signupLoading">Cancel</v-btn>
            <v-btn color="primary" :loading="signupLoading" @click="startContributionSignup">
              Continue
            </v-btn>
          </template>
          <template v-else-if="contributionDialogStep === 'verify'">
            <v-btn variant="text" @click="closeContributionDialog" :disabled="verifyingCode || contributionSaving">Cancel</v-btn>
            <v-btn color="primary" :loading="verifyingCode || contributionSaving" @click="verifyContributionCode">
              Verify & Submit
            </v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" @click="closeContributionDialog">Close</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="shareCopied" color="success" timeout="2500">
      Link copied to clipboard.
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiService from '../services/api'
import { isSupportedImage, normalizeImageFile } from '../services/photoUpload'
import { useMemorialStore } from '../stores/memorial'
import ProfileMenu from '../components/ProfileMenu.vue'
import PostContributionSupport from '../components/PostContributionSupport.vue'
import AudioEntry from '../components/media/AudioEntry.vue'
import VoiceContributionInput from '../components/media/VoiceContributionInput.vue'
import { formatLifeDate, sleep } from '../utils/helpers'

const route = useRoute()
const router = useRouter()
const slug = route.params.slug
const store = useMemorialStore()

const loading = ref(true)
const error = ref('')
const memorial = ref(null)
const userContribution = ref(null)
const userRsvp = ref(null)

const contributorName = ref('')
const contributorEmail = ref('')
const contributorMessage = ref('')
const contributionPhotos = ref([])
const contributionAudios = ref([])
const existingContributionAudios = ref([])
const removedPhotoIds = ref([])
const fileInput = ref(null)
const contributionError = ref('')
const contributionSuccess = ref('')
const contributionSubmitting = ref(false)
const messageError = ref('')
const messageSuccess = ref('')
const editContributionOpen = ref(false)
const showContributionSection = ref(false)
const showRsvpSection = ref(false)
// Revealed once a contribution lands, so the charity and sponsorship asks follow it
const showSupportAsk = ref(false)

const rsvpName = ref('')
const rsvpEmail = ref('')
const rsvpEmailError = ref(false)

const showContributionDialog = ref(false)
const contributionDialogStep = ref('credentials')
const contributorPassword = ref('')
const contributorPasswordConfirm = ref('')
const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const contributionDialogError = ref('')
const signupToken = ref('')
const signupLoading = ref(false)
const verificationCode = ref('')
const verifyingCode = ref(false)
const contributionSaving = ref(false)
const contributionCompletedMessage = ref('')
const resendingCode = ref(false)
const resendCooldown = ref(0)
let resendTimer = null
const shareCopied = ref(false)

// QR mode — set when the page is opened via a scanned QR code (?qrt=token)
const qrToken = ref(route.query.qrt || '')
const qrMode = computed(() => !!qrToken.value)
const qrSubmitting = ref(false)
const qrSuccess = ref(false)
const qrError = ref('')
const qrRequiresSignIn = ref(false)
const qrFileInput = ref(null)

const rsvpStatus = ref('')
const rsvpMessage = ref('')
const rsvpSubmitting = ref(false)
const rsvpSuccess = ref(false)
const rsvpResponse = ref('')
const rsvpError = ref('')

const rsvpOptions = [
  { label: 'Will attend', value: 'ATTENDING' },
  { label: 'Not attending', value: 'NOT_ATTENDING' },
  { label: 'Not sure', value: 'UNSURE' }
]

const mapExpanded = ref(false)
const detailsExpanded = ref(false)

const mapSrc = computed(() => {
  if (!memorial.value?.address || !memorial.value.address.trim()) return ''
  return `https://www.google.com/maps?q=${encodeURIComponent(memorial.value.address.trim())}&output=embed`
})

const detailsHasMore = computed(() => {
  if (!memorial.value?.details) return false
  const lines = memorial.value.details.split('\n').filter(line => line.trim())
  return lines.length > 1
})

const loggedInEmail = computed(() => {
  return store.userEmail || store.user?.email || ''
})

const loggedInName = computed(() => {
  const firstName = store.user?.firstName || ''
  const surname = store.user?.surname || ''
  const fullName = `${firstName} ${surname}`.trim()
  return fullName || store.user?.email || ''
})

const isAuthenticated = computed(() => {
  return !!store.apiToken
})

const hasDonationSetup = computed(() => {
  return !!memorial.value?.memorialFunds?.[0]?.title
})

// Memorials created before events became optional have no flag and always had one
const hasEvent = computed(() => {
  return memorial.value?.hasEvent !== false
})

const goToDonate = () => {
  router.push(`/donate/${slug}`)
}

const contributionEmailError = computed(() => {
  if (!contributorEmail.value) return false
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return !emailPattern.test(contributorEmail.value)
})


const isUserContributionApproved = computed(() => {
  return !!(userContribution.value?.dateApproved || userContribution.value?.dateSeen)
})

const userContributionClass = computed(() => {
  return isUserContributionApproved.value ? 'contribution-approved' : 'contribution-pending'
})

const ownerPhotos = computed(() => {
  if (!memorial.value?.media) return []
  const profileUuid = memorial.value?.profilePhoto?.uuid
  return memorial.value.media.filter(m => m.uuid !== profileUuid && (m.mediaType === 0 || m.mediaType == null))
})

const ownerAudios = computed(() => memorial.value?.media?.filter(media => media.mediaType === 4) || [])
const mediaPhotos = media => (media || []).filter(item => item.mediaType === 0 || item.mediaType == null)
const mediaAudios = media => (media || []).filter(item => item.mediaType === 4)

const ownerFirstName = computed(() => memorial.value?.user?.firstName || 'The Family')

const ownerStories = computed(() => {
  return memorial.value?.stories || []
})

const sortedContributions = computed(() => {
  if (!memorial.value?.contributions) return []
  const userId = userContribution.value?.id
  return memorial.value.contributions
    .filter(c => !userId || c.id !== userId)
    .sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated))
})

const loadMemorial = async (preserveUi = false) => {
  loading.value = true
  error.value = ''

  try {
    const response = await apiService.getPublicMemorial(slug)
    if (response.success && response.memorial) {
      memorial.value = response.memorial
      userContribution.value = response.userContribution || null
      userRsvp.value = response.userRsvp || null

      contributorName.value = ''
      contributorEmail.value = ''
      contributorMessage.value = ''
      contributionPhotos.value = []
      contributionAudios.value = []
      existingContributionAudios.value = []
      removedPhotoIds.value = []
      contributionError.value = ''
      contributionSuccess.value = ''
      messageError.value = ''
      messageSuccess.value = ''
      editContributionOpen.value = false

      if (userRsvp.value) {
        rsvpStatus.value = userRsvp.value.status || ''
        rsvpMessage.value = userRsvp.value.message || ''
      } else {
        rsvpStatus.value = ''
        rsvpMessage.value = ''
      }

      if (isAuthenticated.value) {
        rsvpName.value = userRsvp.value?.name || loggedInName.value
        rsvpEmail.value = userRsvp.value?.email || loggedInEmail.value
        rsvpEmailError.value = false
      } else {
        rsvpName.value = ''
        rsvpEmail.value = ''
        rsvpEmailError.value = false
      }

      if (userContribution.value) {
        contributorName.value = userContribution.value.contributorName || ''
        contributorEmail.value = userContribution.value.contributorEmail || ''
        contributorMessage.value = userContribution.value.message || ''
        if (userContribution.value.media && userContribution.value.media.length > 0) {
          userContribution.value.media.filter(media => media.mediaType === 0 || media.mediaType == null).forEach(media => {
            const photoUrl = `${media.location}/${media.filename}.${media.extension}`
            contributionPhotos.value.push({
              id: media.uuid,
              caption: media.title || '',
              preview: photoUrl,
              existing: true
            })
          })
          existingContributionAudios.value = userContribution.value.media.filter(media => media.mediaType === 4)
        }
        if (!preserveUi) {
          showContributionSection.value = false
        }
      }

      if (!memorial.value?.address) {
        mapExpanded.value = false
      }
      detailsExpanded.value = false
    } else {
      error.value = response.msg || 'This memory may have been removed or the link might be incorrect.'
    }
  } catch (err) {
    console.error('Feed load error:', err)
    error.value = 'Unable to load memory. Please try again.'
  } finally {
    loading.value = false
  }
}

const reloadMemorialPreserveUi = async () => {
  const scrollY = window.scrollY
  const contributionOpen = showContributionSection.value
  const rsvpOpen = showRsvpSection.value

  await loadMemorial(true)
  await nextTick()

  showContributionSection.value = contributionOpen
  showRsvpSection.value = rsvpOpen

  window.scrollTo({ top: scrollY, behavior: 'auto' })
}

const copyShareLink = async () => {
  const url = `${window.location.origin}/for/${slug}`
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url)
    } else {
      const temp = document.createElement('input')
      temp.value = url
      document.body.appendChild(temp)
      temp.select()
      document.execCommand('copy')
      document.body.removeChild(temp)
    }
    shareCopied.value = true
  } catch (err) {
    console.error('Copy link failed:', err)
  }
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatMemorialDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatRsvpDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'today'
  } else if (diffDays === 1) {
    return 'yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)} weeks ago`
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getMediaUrl = (media) => {
  if (media.location) {
    return `${media.location}/${media.filename}.${media.extension}`
  }
  return `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`
}

const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const goToLogin = () => {
  router.push({ name: 'Login', query: { redirect: `/for/${slug}` } })
}

const toggleGuestContribution = () => {
  showContributionSection.value = !showContributionSection.value
}

const revealSupportAsk = async () => {
  if (!hasDonationSetup.value) {
    showSupportAsk.value = false
    return
  }

  showSupportAsk.value = true
  await nextTick()
  document.getElementById('support-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const toggleRsvpSection = () => {
  showRsvpSection.value = !showRsvpSection.value
}

const toggleEditContribution = () => {
  editContributionOpen.value = !editContributionOpen.value
}

const toggleMap = () => {
  mapExpanded.value = !mapExpanded.value
}

const toggleDetails = () => {
  detailsExpanded.value = !detailsExpanded.value
}

const triggerFileSelect = () => {
  fileInput.value?.click()
}

const onManualFileSelect = (event) => {
  const files = Array.from(event.target.files || []).filter(file => isSupportedImage(file))
  if (files.length) {
    onPhotoSelect(files)
  }
  event.target.value = ''
}

const handleDrop = (event) => {
  const files = Array.from(event.dataTransfer?.files || []).filter(file => isSupportedImage(file))
  if (files.length) {
    onPhotoSelect(files)
  }
}

const onPhotoSelect = async (files) => {
  for (const file of files) {
    if (!isSupportedImage(file)) continue

    try {
      const processedFile = await normalizeImageFile(file)
      const previewUrl = URL.createObjectURL(processedFile)
      contributionPhotos.value.push({
        id: `${Date.now()}-${processedFile.name}`,
        file: processedFile,
        preview: previewUrl,
        caption: ''
      })
    } catch (error) {
      console.error('Error processing file:', error)
      contributionError.value = `Error processing ${file.name}. Please try a different file format.`
    }
  }
}

const removePhoto = (index) => {
  const photo = contributionPhotos.value.splice(index, 1)[0]
  if (!photo) return
  if (photo.existing && photo.id) {
    removedPhotoIds.value.push(photo.id)
  }
  if (photo.preview && !photo.existing) {
    URL.revokeObjectURL(photo.preview)
  }
}

const removeExistingAudio = index => {
  const audio = existingContributionAudios.value.splice(index, 1)[0]
  if (audio?.uuid) removedPhotoIds.value.push(audio.uuid)
}

const validateRsvpEmail = () => {
  if (!rsvpEmail.value) {
    rsvpEmailError.value = false
    return
  }
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  rsvpEmailError.value = !emailPattern.test(rsvpEmail.value)
}

const sendRsvp = async () => {
  if (!memorial.value?.shareSlug || !rsvpStatus.value) return
  if (!isAuthenticated.value) {
    if (!rsvpEmail.value) {
      rsvpError.value = 'Please enter a valid email address'
      return
    }
    validateRsvpEmail()
    if (rsvpEmailError.value) {
      rsvpError.value = 'Please enter a valid email address'
      return
    }
  }
  rsvpSubmitting.value = true
  rsvpError.value = ''
  rsvpSuccess.value = false

  try {
    const payload = {
      name: rsvpName.value || loggedInName.value || '',
      email: rsvpEmail.value || loggedInEmail.value || '',
      status: rsvpStatus.value,
      message: rsvpMessage.value || ''
    }
    const response = await apiService.submitRsvp(memorial.value.shareSlug, payload)
    if (response.success) {
      rsvpSuccess.value = true
      rsvpResponse.value = response.msg || 'Thank you for letting us know.'
      await reloadMemorialPreserveUi()
    } else {
      rsvpError.value = response.msg || 'We could not record your RSVP. Please try again.'
    }
  } catch (err) {
    console.error('RSVP error:', err)
    rsvpError.value = 'We could not record your RSVP. Please try again.'
  } finally {
    rsvpSubmitting.value = false
  }
}

const submitContributionData = async (token) => {
  if (!token || !memorial.value?.shareSlug) {
    throw new Error('Missing auth token or memory identifier')
  }

  contributionSaving.value = true

  const formData = new FormData()
  formData.append('shareSlug', memorial.value.shareSlug)
  const resolvedName = (contributorName.value || '').trim()
    || loggedInName.value
    || loggedInEmail.value
    || userContribution.value?.contributorName
    || ''
  const resolvedEmail = (contributorEmail.value || '').trim()
    || loggedInEmail.value
    || userContribution.value?.contributorEmail
    || ''
  formData.append('contributorName', resolvedName)
  formData.append('contributorEmail', resolvedEmail)
  formData.append('contributorMessage', contributorMessage.value || '')

  if (removedPhotoIds.value.length > 0) {
    removedPhotoIds.value.forEach(photoId => {
      formData.append('removedPhotoIds', photoId)
    })
  }

  contributionPhotos.value.forEach(photo => {
    if (photo.existing && photo.id) {
      formData.append('existingPhotoIds', photo.id)
      formData.append('existingPhotoCaptions', photo.caption || '')
    }
  })

  contributionAudios.value.forEach(audio => {
    if (audio.file) {
      formData.append('audios', audio.file)
      formData.append('audioCaptions', audio.caption || '')
    }
  })

  contributionPhotos.value.forEach(photo => {
    if (!photo.existing && photo.file) {
      formData.append('photos', photo.file)
      formData.append('photoCaptions', photo.caption || '')
    }
  })

  try {
    const response = await apiService.submitContribution(formData, token)
    if (!response.success) {
      throw new Error(response.msg || 'We were unable to save your contribution.')
    }
  } finally {
    contributionSaving.value = false
  }
}

const openContributionDialog = async () => {
  contributionError.value = ''
  contributionSuccess.value = ''

  if (!isAuthenticated.value) {
    if (!contributorName.value || contributorName.value.trim().length === 0) {
      contributionError.value = 'Please enter your name.'
      return
    }
    if (!contributorEmail.value || contributionEmailError.value) {
      contributionError.value = 'Please enter a valid email address.'
      return
    }
  } else {
    if (!contributorName.value || contributorName.value.trim().length === 0) {
      contributorName.value = loggedInName.value || loggedInEmail.value
    }
    if (!contributorEmail.value) {
      contributorEmail.value = loggedInEmail.value
    }
  }
  if (!contributorMessage.value && contributionPhotos.value.length === 0 && contributionAudios.value.length === 0 && existingContributionAudios.value.length === 0) {
    contributionError.value = 'Please include a message, photo, or voice memory.'
    return
  }

  contributionDialogStep.value = 'credentials'
  contributionDialogError.value = ''
  contributorPassword.value = ''
  contributorPasswordConfirm.value = ''
  verificationCode.value = ''
  signupToken.value = ''
  showContributionDialog.value = true
  contributionSubmitting.value = true
}

const closeContributionDialog = () => {
  if (signupLoading.value || verifyingCode.value || contributionSaving.value) return
  showContributionDialog.value = false
  contributionSubmitting.value = false
}

const startContributionSignup = async () => {
  if (contributorPassword.value.length < 6) {
    contributionDialogError.value = 'Password must be at least 6 characters long.'
    return
  }

  if (contributorPassword.value !== contributorPasswordConfirm.value) {
    contributionDialogError.value = 'Passwords do not match.'
    return
  }

  contributionDialogError.value = ''
  signupLoading.value = true

  try {
    const response = await apiService.signUp(contributorEmail.value, contributorPassword.value, null, contributorName.value)
    if (response.success || (response.pendingVerification && response.token)) {
      await sleep(500)
      signupToken.value = response.token
      contributionDialogStep.value = 'verify'
    } else {
      contributionDialogError.value = response.msg || 'Unable to start verification. Please try again.'
    }
  } catch (err) {
    console.error('Contribution signup error:', err)
    contributionDialogError.value = 'Unable to start verification. Please try again.'
  } finally {
    signupLoading.value = false
  }
}

const verifyContributionCode = async () => {
  if (!verificationCode.value) {
    contributionDialogError.value = 'Enter the verification code that was emailed to you.'
    return
  }

  verifyingCode.value = true
  contributionDialogError.value = ''

  try {
    const response = await apiService.confirmCode(verificationCode.value, signupToken.value)
    if (response.success) {
      apiService.setToken(signupToken.value)
      store.setApiAuth(signupToken.value, {
        email: contributorEmail.value || ''
      })
      sessionStorage.setItem('contribution_authenticated', 'true')
      await sleep(500)
      try {
        await submitContributionData(signupToken.value)
        contributionCompletedMessage.value = 'Thank you for contributing. This will be sent to the administrator of this memory for approval before posting to the public feed'
        contributionSuccess.value = contributionCompletedMessage.value
        contributionDialogStep.value = 'success'
        contributionSubmitting.value = false
        await reloadMemorialPreserveUi()
        showContributionSection.value = false
        showRsvpSection.value = false
        revealSupportAsk()
      } catch (submitError) {
        contributionDialogError.value = submitError.message || 'We were unable to save your contribution.'
        contributionDialogStep.value = 'verify'
      }
    } else {
      contributionDialogError.value = response.msg || 'The verification code is incorrect.'
    }
  } catch (err) {
    console.error('Contribution verification error:', err)
    contributionDialogError.value = 'We could not verify the code. Please try again.'
  } finally {
    verifyingCode.value = false
  }
}

const startResendCooldown = () => {
  resendCooldown.value = 30
  resendTimer = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      clearInterval(resendTimer)
      resendTimer = null
    }
  }, 1000)
}

const resendContributionCode = async () => {
  if (resendCooldown.value > 0 || resendingCode.value) return

  resendingCode.value = true
  contributionDialogError.value = ''

  try {
    const response = await apiService.resendCode(signupToken.value)
    if (response.success) {
      startResendCooldown()
    } else {
      contributionDialogError.value = response.msg || 'Unable to resend the code. Please try again.'
    }
  } catch (err) {
    console.error('Resend code error:', err)
    contributionDialogError.value = 'Unable to resend the code. Please try again.'
  } finally {
    resendingCode.value = false
  }
}

const saveContribution = async () => {
  contributionError.value = ''
  contributionSuccess.value = ''

  if (contributionPhotos.value.length === 0 && contributionAudios.value.length === 0 && existingContributionAudios.value.length === 0 && (!contributorMessage.value || contributorMessage.value.trim().length === 0)) {
    contributionError.value = 'Please include a message, photo, or voice memory.'
    return
  }

  contributionSubmitting.value = true

  try {
    await submitContributionData(apiService.getToken())
    contributionSuccess.value = 'Contribution saved.'
    showContributionSection.value = false
    editContributionOpen.value = false
    await reloadMemorialPreserveUi()
    revealSupportAsk()
  } catch (err) {
    console.error('Contribution save error:', err)
    contributionError.value = err.message || 'We were unable to save your contribution.'
  } finally {
    contributionSubmitting.value = false
  }
}

const handleQrContribution = async () => {
  qrError.value = ''
  qrRequiresSignIn.value = false

  if (!contributorName.value?.trim()) {
    qrError.value = 'Please enter your name.'
    return
  }
  if (!contributorEmail.value?.trim()) {
    qrError.value = 'Please enter your email address.'
    return
  }

  qrSubmitting.value = true
  try {
    const formData = new FormData()
    formData.append('qrToken', qrToken.value)
    formData.append('name', contributorName.value.trim())
    formData.append('email', contributorEmail.value.trim())
    formData.append('message', contributorMessage.value?.trim() || '')

    contributionPhotos.value.forEach(photo => {
      if (!photo.existing && photo.file) {
        formData.append('photos', photo.file)
        formData.append('photoCaptions', photo.caption || '')
      }
    })

    contributionAudios.value.forEach(audio => {
      if (audio.file) {
        formData.append('audios', audio.file)
        formData.append('audioCaptions', audio.caption || '')
      }
    })

    const response = await apiService.submitQrContribution(formData)
    if (response.success) {
      if (response.token) {
        apiService.setToken(response.token)
        store.setApiAuth(response.token, null)
      }
      qrSuccess.value = true
      showContributionSection.value = false
      await reloadMemorialPreserveUi()
      revealSupportAsk()
    } else if (response.requiresSignIn) {
      qrRequiresSignIn.value = true
    } else {
      qrError.value = response.msg || 'Something went wrong. Please try again.'
    }
  } catch (err) {
    console.error('QR contribution error:', err)
    qrError.value = 'An unexpected error occurred. Please try again.'
  } finally {
    qrSubmitting.value = false
  }
}

const goToLoginForQr = () => {
  router.push({ name: 'Login', query: { redirect: route.fullPath } })
}

onMounted(async () => {
  await loadMemorial()
  if (qrMode.value) {
    // Auto-open contribution section and scroll to it
    showContributionSection.value = true
    await nextTick()
    const el = document.getElementById('qr-contribute-section')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
})

onBeforeUnmount(() => {
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }
})
</script>

<style scoped>
.feed-page {
  min-height: 100vh;
}

/* ── Band structure ── */
.band {
  width: 100%;
}

.band-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.band-hero {
  background: var(--v-theme-background);
}

.band-hero .band-content {
  padding-bottom: 1rem;
}

.band-details {
  background: #1a1a1a;
  color: #e0e0e0;
}

.band-red-stripe {
  width: 100%;
  height: 30px;
  background: #D4401A;
}

.band-host {
  background: #ffffff;
}

.band-contributions {
  background: #ffffff;
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

/* The brand is text, not an image — a fixed height just makes the glyphs
   overflow the box and throws off the profile icon's vertical centring. */
.brand-logo-svg {
  height: auto;
  width: auto;
  max-width: 100%;
  min-width: 0;
}

.panel-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 1rem;
  width: 100%;
  padding: 0 2rem;
}

.panel-btn {
  white-space: nowrap;
}

.rsvp-button-status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: 0.6rem;
  font-size: 0.75rem;
  color: rgb(var(--v-theme-success));
}

.panel-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  padding: 0 2rem;
}

.mobile-logo-link {
  display: none;
}

.mobile-logo {
  height: 36px;
  width: auto;
}

.hero-layout {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;
}

.hero-info {
  text-align: left;
  max-width: 520px;
  width: 100%;
  align-self: flex-start;
}

.hero-photo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 0;
}

.hero-photo {
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.hero-photo-placeholder {
  width: 300px;
  height: 300px;
  background: var(--v-theme-gray100);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.remembering-title {
  font-family: var(--v-theme-headingFont);
  font-size: 0.95rem;
  color: var(--v-theme-gray600);
  text-align: left;
  margin-bottom: 0.35rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.hero-name {
  font-family: var(--v-theme-headingFont);
  font-size: 2.35rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0 0 0.6rem 0;
  text-align: left;
}

.details-inner {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.life-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.75rem;
  margin: 0 0 0.35rem;
  color: #ffffff;
  font-family: var(--v-theme-headingFont);
  font-size: 2rem;
  font-weight: 400;
  line-height: 1.2;
}

.life-heading span {
  color: #e0e0e0;
  font-size: 1.15rem;
  white-space: nowrap;
}

.hero-detail-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #e0e0e0;
  font-size: 1rem;
}

.address-detail {
  flex: 1;
  min-width: 0;
}

.address-detail span {
  display: inline;
}

.address-toggle {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.details-top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.details-date-address {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
  min-width: 0;
}

.hero-details-text {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 0.15rem;
  text-align: left;
  line-height: 1.5;
  color: #bdbdbd;
  font-size: 0.95rem;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.hero-map {
  max-width: 800px;
  margin: 0 auto;
}

.map-big-icon {
  width: 64px;
  height: 64px;
  display: block;
  cursor: pointer;
  flex-shrink: 0;
  object-fit: contain;
}

.details-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.details-toggle {
  border: none;
  background: none;
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

:deep(.rsvp-toggle-group) {
  gap: 0.5rem;
}

:deep(.rsvp-option-btn) {
  justify-content: flex-start;
  text-align: left;
  min-height: 48px;
  padding: 0.75rem 1rem;
  background-color: var(--v-theme-surface);
}

:deep(.rsvp-option-btn .v-btn__overlay) {
  opacity: 0;
}

:deep(.rsvp-option-btn .v-btn__underlay) {
  opacity: 0;
}

:deep(.rsvp-option-btn .v-btn__content) {
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
}

:deep(.rsvp-option-btn .v-icon) {
  margin-right: 0.5rem;
}

/* Feed Content */
.feed-content {
  max-width: 800px;
  margin: 0 auto;
}

.section-title {
  font-family: var(--v-theme-headingFont);
  font-size: 1.5rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.2);
}

.rsvp-title {
  cursor: pointer;
}

.contribute-intro {
  max-width: 640px;
}

/* Photos List - Photo left, caption right */
.photos-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.photo-row {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.photo-left {
  flex-shrink: 0;
  width: 280px;
}

.photo-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

.photo-right {
  flex: 1;
  padding-top: 0.25rem;
}

.photo-caption {
  font-size: 1rem;
  color: var(--v-theme-gray600);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

/* Stories Section - Full width */
.stories-section {
  width: 100%;
}

.story-item {
  padding: 1rem 0;
}

.story-text {
  color: var(--v-theme-gray600);
  line-height: 1.7;
  white-space: pre-wrap;
  margin: 0;
}

/* Contribution Section */
.contribution-section {
  position: relative;
  padding: 0;
  background: white;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.user-contribution.contribution-pending {
  opacity: 0.7;
  border-color: rgb(var(--v-theme-warning));
  padding: 10px;
}

.user-contribution.contribution-approved {
  border-color: rgb(var(--v-theme-success));
  padding: 10px;
}

/* Contributor Header */
.contributor-header {
  display: flex;
  align-items: center;
}

.contributor-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--v-theme-gray600);
}

.contributor-date {
  color: var(--v-theme-gray500);
}

.contribution-message {
  background: rgba(var(--v-theme-primary), 0.05);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.contribution-message p {
  margin: 0;
  color: var(--v-theme-gray600);
  line-height: 1.6;
  white-space: pre-wrap;
}

.upload-area {
  padding: 1.5rem;
  border: 1px dashed var(--v-theme-gray600);
  border-radius: 12px;
  text-align: center;
  background: var(--v-theme-background);
  cursor: pointer;
}

.upload-button {
  border: none;
  background: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}

.upload-hidden-input {
  display: none;
}

.photo-preview-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.photo-preview-row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 1rem;
  align-items: start;
}

.preview-details {
  align-self: start;
}

.preview-details :deep(.v-input) {
  margin-top: 0;
}

.preview-thumb img {
  width: 100%;
  border-radius: 8px;
}

/* Empty State */
.empty-state {
  padding: 4rem 2rem;
}

/* Responsive */
@media (max-width: 600px) {
  .for-container {
    padding-top: 1rem !important;
    padding-bottom: 2rem !important;
  }

  .page-header {
    margin-bottom: 0 !important;
  }

  .memorial-hero {
    padding-top: 0;
  }

  .hero-layout {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
  }

  .hero-info {
    text-align: center;
    max-width: 100%;
  }

  .remembering-title,
  .hero-name {
    text-align: center;
  }

  .brand-header {
    display: none;
  }

  /* fixed (not absolute) so it stays in the screen corner now that .page-header
     is a positioned ancestor */
  .mobile-logo-link {
    display: inline-flex;
    position: fixed;
    top: 0.25rem;
    left: 0.25rem;
    z-index: 1002;
  }

  .address-toggle {
    width: 100%;
  }

  .panel-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0 1rem;
  }

  .panel-btn {
    width: 100%;
  }

  .panel-actions {
    flex-direction: column;
    align-items: stretch;
    padding: 0 1rem;
  }

  .panel-title-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .panel-title-right {
    white-space: normal;
  }

  .hero-photo,
  .hero-photo-placeholder {
    width: 220px;
    height: 220px;
  }

  .hero-name {
    font-size: 1.75rem;
    margin-bottom: 0.25rem;
  }

  .remembering-title {
    margin-bottom: 0.2rem;
  }

  .hero-detail-item {
    font-size: 0.9rem;
  }

  .map-big-icon {
    width: 48px;
    height: 48px;
  }

  :deep(.rsvp-toggle-group) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible;
    width: 100%;
    padding-bottom: 0.5rem;
  }

  :deep(.rsvp-actions) {
    height: auto;
    overflow: visible;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .photo-row {
    flex-direction: column;
    gap: 0.75rem;
  }

  .photo-left {
    width: 100%;
  }

  .photo-right {
    padding-top: 0;
  }

  .contributor-header {
    flex-wrap: wrap;
  }

  .photo-preview-row {
    grid-template-columns: 1fr;
  }
}
</style>
